import React, { useEffect, useState } from "react";
import { Alert, Box, LinearProgress, MenuItem, Snackbar, TextField, Typography } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SearchIcon from "@mui/icons-material/Search";
import { useSnackbar } from "../../hooks/useSnackbar";
import { Panel, FormGrid, PrimaryButton, GhostButton } from "./ui";
import {
  BALANCE_TYPES,
  BALANCE_LOAD_TYPES,
  PAY_RESULT_SUCCESS,
  formatKurus,
  parseKurus,
  DP,
} from "./constants";
import { loadCardBallance, getCardBallance } from "../../api/digitalPayAPI";
import {
  BALANCE_TEMPLATE,
  downloadTemplate,
  readSheet,
  missingHeaders,
} from "./excelTemplates";
import { isSuperAdminUser } from "./roleUtils";

export default function PaymentsPanel({ lookups }) {
  const { firms = [] } = lookups;
  const canSelectFirm = isSuperAdminUser();
  const assignedFirmName = canSelectFirm ? "" : (firms[0]?.name ?? "");
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  const [single, setSingle] = useState({
    sicilNo: "",
    firmName: assignedFirmName,
    balanceType: BALANCE_TYPES[0].value,
    balanceLoadType: BALANCE_LOAD_TYPES[0].value,
    price: "",
  });
  const [busy, setBusy] = useState(false);

  const [query, setQuery] = useState({ cardNo: "", firmName: assignedFirmName });

  const [mass, setMass] = useState({
    firmName: assignedFirmName,
    balanceType: BALANCE_TYPES[0].value,
    balanceLoadType: BALANCE_LOAD_TYPES[0].value,
  });
  const [bulk, setBulk] = useState(null);

  useEffect(() => {
    if (!canSelectFirm) {
      setSingle((current) => ({ ...current, firmName: assignedFirmName }));
      setQuery((current) => ({ ...current, firmName: assignedFirmName }));
      setMass((current) => ({ ...current, firmName: assignedFirmName }));
    }
  }, [assignedFirmName, canSelectFirm]);

  // --- Tekil yükleme ---
  const handleSingleLoad = async () => {
    const price = parseKurus(single.price);
    if (!single.sicilNo || !single.firmName || price <= 0) {
      showSnackbar("Sicil no, firma ve geçerli bir tutar zorunlu", "error");
      return;
    }
    setBusy(true);
    try {
      const res = await loadCardBallance({
        balanceType: Number(single.balanceType),
        balanceLoadType: Number(single.balanceLoadType),
        sicilNo: single.sicilNo,
        firmName: single.firmName,
        price,
      });
      if (res?.result !== PAY_RESULT_SUCCESS) {
        showSnackbar("Bakiye yüklenemedi", "error");
        return;
      }
      showSnackbar(
        `Bakiye yüklendi. Standart: ${formatKurus(res.remainingBalance)} ₺ · İSG: ${formatKurus(res.remainingIsgBalance)} ₺`,
        "success"
      );
      setSingle({ ...single, sicilNo: "", price: "" });
    } catch (e) {
      showSnackbar(e.message || "Bakiye yüklenemedi", "error");
    } finally {
      setBusy(false);
    }
  };

  // --- Bakiye sorgulama ---
  const handleQuery = async () => {
    if (!query.cardNo || !query.firmName) {
      showSnackbar("Kart no ve firma zorunlu", "error");
      return;
    }
    try {
      const b = await getCardBallance(query.cardNo, query.firmName);
      showSnackbar(
        `Standart: ${formatKurus(b.standartBallance)} ₺ · İSG: ${formatKurus(b.isgBallance)} ₺`,
        "info"
      );
    } catch (e) {
      showSnackbar(e.message || "Bakiye alınamadı", "error");
    }
  };

  // --- Toplu yükleme (BakiyeYüklemeŞablonu.xlsx) ---
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!mass.firmName) {
      showSnackbar("Önce firma seçin — şablonda firma sütunu yok", "error");
      return;
    }

    let sheet;
    try {
      sheet = await readSheet(file);
    } catch (err) {
      showSnackbar(err.message, "error");
      return;
    }

    const missing = missingHeaders(sheet, BALANCE_TEMPLATE);
    if (missing.length) {
      showSnackbar(`Şablon uyumsuz. Eksik sütun: ${missing.join(", ")}`, "error");
      return;
    }

    let ok = 0;
    const failed = [];
    setBulk({ done: 0, total: sheet.length });

    // Sıralı: LoadCardBallance döngüsü (demo-ui ile aynı akış).
    for (let i = 0; i < sheet.length; i++) {
      const r = sheet[i];
      // Şablondaki tutar zaten kuruş ("Bakiye(Kuruş)"), tekrar çevirmiyoruz.
      const price = Math.round(Number(r["Bakiye(Kuruş)"]));
      try {
        if (!Number.isFinite(price) || price <= 0) throw new Error("Geçersiz tutar");
        const res = await loadCardBallance({
          balanceType: Number(mass.balanceType),
          balanceLoadType: Number(mass.balanceLoadType),
          sicilNo: String(r["Sicil No"]),
          firmName: mass.firmName,
          price,
        });
        if (res?.result !== PAY_RESULT_SUCCESS) throw new Error("Reddedildi");
        ok++;
      } catch (err) {
        failed.push(`${r["Sicil No"]}: ${err.message}`);
      }
      setBulk({ done: i + 1, total: sheet.length });
    }

    setBulk(null);

    if (failed.length) {
      console.warn("[Bakiye toplu yükleme] hatalı satırlar:", failed);
      showSnackbar(
        `Toplu yükleme bitti. Başarılı: ${ok}, hatalı: ${failed.length}. İlk hata: ${failed[0]}`,
        "warning"
      );
    } else {
      showSnackbar(`Toplu yükleme tamamlandı. ${ok} karta bakiye yüklendi.`, "success");
    }
  };

  const firmSelect = (value, onChange, label = "Firma") =>
    canSelectFirm ? (
      <TextField select label={label} value={value} onChange={onChange} size="small" fullWidth>
        {firms.map((f) => (
          <MenuItem key={f.idfirm} value={f.name}>
            {f.name}
          </MenuItem>
        ))}
      </TextField>
    ) : null;

  return (
    <>
      <Panel
        title="Tekil Bakiye Yükleme"
        sub="Seçtiğiniz karta güvenli bir şekilde bakiye yükleyebilirsiniz."
      >
        <FormGrid>
          <TextField
            label="Sicil No"
            value={single.sicilNo}
            onChange={(e) => setSingle({ ...single, sicilNo: e.target.value })}
            size="small"
            required
            fullWidth
          />
          {firmSelect(single.firmName, (e) =>
            setSingle({ ...single, firmName: e.target.value })
          )}
          <TextField
            select
            label="Bakiye Tipi"
            value={single.balanceType}
            onChange={(e) => setSingle({ ...single, balanceType: e.target.value })}
            size="small"
            fullWidth
          >
            {BALANCE_TYPES.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Devir Tipi"
            value={single.balanceLoadType}
            onChange={(e) => setSingle({ ...single, balanceLoadType: e.target.value })}
            size="small"
            fullWidth
          >
            {BALANCE_LOAD_TYPES.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Tutar"
            value={single.price}
            onChange={(e) => setSingle({ ...single, price: e.target.value })}
            size="small"
            fullWidth
            helperText="₺ — örn. 4,80"
          />
        </FormGrid>

        <Box sx={{ mt: 2.5 }}>
          <PrimaryButton onClick={handleSingleLoad} disabled={busy}>
            Bakiye Yükle
          </PrimaryButton>
        </Box>
      </Panel>

      <Panel
        title="Bakiye Sorgulama"
        sub="Kartın güncel standart ve İSG bakiyesini görüntüleyin."
      >
        <FormGrid>
          <TextField
            label="Kart No"
            value={query.cardNo}
            onChange={(e) => setQuery({ ...query, cardNo: e.target.value })}
            size="small"
            fullWidth
          />
          {firmSelect(query.firmName, (e) => setQuery({ ...query, firmName: e.target.value }))}
        </FormGrid>
        <Box sx={{ mt: 2.5 }}>
          <GhostButton startIcon={<SearchIcon />} onClick={handleQuery}>
            Sorgula
          </GhostButton>
        </Box>
      </Panel>

      <Panel
        title="Toplu Bakiye Yükleme"
        sub="BakiyeYüklemeŞablonu.xlsx — Sicil No · Adı · Soyadı · Bakiye(Kuruş)"
        action={
          <GhostButton
            startIcon={<DownloadIcon />}
            onClick={() => downloadTemplate(BALANCE_TEMPLATE)}
          >
            Şablonu İndir
          </GhostButton>
        }
      >
        <FormGrid>
          {firmSelect(
            mass.firmName,
            (e) => setMass({ ...mass, firmName: e.target.value }),
            "Firma (zorunlu)"
          )}
          <TextField
            select
            label="Bakiye Tipi"
            value={mass.balanceType}
            onChange={(e) => setMass({ ...mass, balanceType: e.target.value })}
            size="small"
            fullWidth
          >
            {BALANCE_TYPES.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Devir Tipi"
            value={mass.balanceLoadType}
            onChange={(e) => setMass({ ...mass, balanceLoadType: e.target.value })}
            size="small"
            fullWidth
          >
            {BALANCE_LOAD_TYPES.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        </FormGrid>

        <Typography sx={{ fontSize: "0.8rem", color: DP.textMuted, mt: 2 }}>
          Şablondaki tutar sütunu <strong>kuruş</strong> cinsindendir (480 = 4,80 ₺).
          {canSelectFirm
            ? " Firma sütunu şablonda yok, yukarıdan seçilir."
            : " Firma hesabınıza göre otomatik belirlenir."}
        </Typography>

        <Box sx={{ mt: 2 }}>
          <PrimaryButton component="label" startIcon={<UploadFileIcon />} disabled={!!bulk}>
            Excel Yükle
            <input type="file" accept=".xlsx,.xls" hidden onChange={handleUpload} />
          </PrimaryButton>
        </Box>

        {bulk && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: "0.85rem", color: DP.textMuted, mb: 0.8 }}>
              Yükleniyor… {bulk.done} / {bulk.total}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(bulk.done / bulk.total) * 100}
              sx={{ borderRadius: 999 }}
            />
          </Box>
        )}
      </Panel>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
